// @ts-nocheck
/**
 * Personalizador de producto — Baskonia / Alavés
 *
 * Handles the Nombre y dorsal customizer block on the PDP.
 * Intercepts the product form submit to add a second line item
 * (personalization product) alongside the base product.
 *
 * DOM entry point: <personalizador-block> custom element rendered by
 * blocks/personalizador.liquid.
 */

class PersonalizadorBlock extends HTMLElement {
  connectedCallback() {
    this._config = JSON.parse(this.querySelector('[data-personalizador-config]').textContent);
    this._mode = 'ninguno';
    this._tipoManual = 'nombre_dorsal';
    this._jugadorSeleccionado = null;
    this._nombreFontCurrent = null;
    this._nombreIsSmall = null;
    this._fontAnimFrame = null;

    this._findExternalElements();
    this._buildJugadoresOptions();
    this._bindEvents();
    this._applyMode('ninguno');
    this._preloadFonts();
  }

  // ─── Font preload ────────────────────────────────────────────────────────────

  async _preloadFonts() {
    const cfg = this._config;
    const fonts = [...new Set([cfg.tipografia_nombre, cfg.tipografia_dorsal].filter(Boolean))];
    try {
      await Promise.all(fonts.map(f => document.fonts.load(`48px "${f}"`)));
      if (this._previewNombre || this._previewDorsal) this._redrawCanvas();
    } catch (_) {}
  }

  // ─── DOM helpers ────────────────────────────────────────────────────────────

  _findExternalElements() {
    // Walk up to the section wrapper (id="shopify-section-{id}")
    let section = this.closest('[id^="shopify-section-"]');
    if (!section) section = document.querySelector('[id^="shopify-section-main"]') || document.body;

    // media-gallery is often in a separate section from the buy box — search document-wide
    this._mediaGallery = section.querySelector('media-gallery')
                       || document.querySelector('media-gallery');
    this._productForm  = section.querySelector('[data-type="add-to-cart-form"]')
                      || document.querySelector('[data-type="add-to-cart-form"]')
                      || document.querySelector('form[action="/cart/add"]');
  }

  _buildJugadoresOptions() {
    const select = this.querySelector('[data-jugadores-select]');
    if (!select) return;

    const cfg = this._config;
    const placeholder = select.querySelector('[data-placeholder]');

    cfg.jugadores.forEach(j => {
      const opt = document.createElement('option');
      opt.value = j.dorsal;
      opt.dataset.nombre = j.nombre;
      opt.dataset.dorsal = j.dorsal;
      opt.textContent = `${j.dorsal} | ${j.nombre}`;
      select.appendChild(opt);
    });

    // reset to placeholder
    select.value = '';
  }

  // ─── Event binding ───────────────────────────────────────────────────────────

  _bindEvents() {
    // Mode radio buttons (Ninguno / Jugador / Personalizado)
    // Use both 'change' and 'click': 'change' fires on first select, 'click' fires even on
    // already-checked radios (e.g. after variant change resets gallery but radio stays checked).
    this.querySelectorAll('[data-mode-radio]').forEach(radio => {
      radio.addEventListener('change', () => this._applyMode(radio.value));
      radio.addEventListener('click', () => this._applyMode(radio.value));
    });

    // Jugador dropdown
    const jugadorSelect = this.querySelector('[data-jugadores-select]');
    if (jugadorSelect) {
      jugadorSelect.addEventListener('change', e => {
        const opt = e.target.selectedOptions[0];
        if (opt && opt.dataset.nombre) {
          this._jugadorSeleccionado = { nombre: opt.dataset.nombre, dorsal: opt.dataset.dorsal };
          this._updatePreview(opt.dataset.nombre, opt.dataset.dorsal);
        } else {
          this._jugadorSeleccionado = null;
          this._clearPreview();
        }
      });
    }

    // Tipo de personalización radio buttons
    this.querySelectorAll('[data-tipo-radio]').forEach(radio => {
      radio.addEventListener('change', () => {
        this._tipoManual = radio.value;
        this._applyTipoManual(radio.value);
        this._updatePreviewFromManualInputs();
      });
    });

    // Nombre / Dorsal text inputs
    const nombreInput = this.querySelector('[data-nombre-input]');
    const dorsalInput = this.querySelector('[data-dorsal-input]');

    if (nombreInput) {
      nombreInput.addEventListener('input', () => this._updatePreviewFromManualInputs());
    }
    if (dorsalInput) {
      dorsalInput.addEventListener('input', () => {
        // Clamp 0–99
        let v = parseInt(dorsalInput.value, 10);
        if (isNaN(v)) v = '';
        else if (v < 0) v = 0;
        else if (v > 99) v = 99;
        dorsalInput.value = v;
        this._updatePreviewFromManualInputs();
      });
    }

    // On variant change, Horizon dispatches 'variant:update' (bubbles) and media-gallery
    // replaces itself entirely. Listen on document to cover cross-section layouts.
    document.addEventListener('variant:update', () => {
      requestAnimationFrame(() => {
        this._findExternalElements();
        this._patchProductFormSubmit();
        if (this._mode === 'ninguno') return;
        if (this._canvas && !this._canvas.isConnected) {
          this._resizeObserver?.disconnect();
          this._resizeObserver = null;
          this._canvas = null;
        }
        this._switchGalleryToPreviewImage();
        requestAnimationFrame(() => this._redrawCanvas());
      });
    });

    // Patch Horizon's handleSubmit so we intercept before their document-level capture delegation
    this._patchProductFormSubmit();
  }

  // ─── Mode logic ──────────────────────────────────────────────────────────────

  _applyMode(mode) {
    this._mode = mode;

    const jugadorPanel = this.querySelector('[data-panel="jugador"]');
    const personalizadoPanel = this.querySelector('[data-panel="personalizado"]');

    jugadorPanel && (jugadorPanel.hidden = mode !== 'jugador');
    personalizadoPanel && (personalizadoPanel.hidden = mode !== 'personalizado');

    if (mode === 'ninguno') {
      this._clearPreview();
    } else if (mode === 'jugador') {
      if (this._jugadorSeleccionado) {
        this._updatePreview(this._jugadorSeleccionado.nombre, this._jugadorSeleccionado.dorsal);
      } else {
        this._switchGalleryToPreviewImage();
        this._clearCanvas();
      }
    } else if (mode === 'personalizado') {
      this._applyTipoManual(this._tipoManual);
      this._switchGalleryToPreviewImage();
      this._updatePreviewFromManualInputs();
    }
  }

  _applyTipoManual(tipo) {
    const nombreGroup = this.querySelector('[data-input-group="nombre"]');
    const dorsalGroup = this.querySelector('[data-input-group="dorsal"]');

    if (!nombreGroup || !dorsalGroup) return;

    nombreGroup.hidden = tipo === 'dorsal';
    dorsalGroup.hidden = tipo === 'nombre';
  }

  _updatePreviewFromManualInputs() {
    if (this._mode !== 'personalizado') return;
    const tipo = this._tipoManual;
    const nombre = tipo !== 'dorsal' ? (this.querySelector('[data-nombre-input]')?.value || '') : '';
    const dorsal = tipo !== 'nombre' ? (this.querySelector('[data-dorsal-input]')?.value || '') : '';
    this._updatePreview(nombre, String(dorsal));
  }

  // ─── Gallery navigation ──────────────────────────────────────────────────────

  _switchGalleryToPreviewImage() {
    if (!this._mediaGallery) return;

    // Horizon uses slideshow-component.select(index)
    const slideshow = this._mediaGallery.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(1);
      return;
    }

    // Fallback: click the 2nd thumbnail button
    const thumbnails = this._mediaGallery.querySelectorAll('.slideshow-controls__thumbnail');
    if (thumbnails.length >= 2) {
      thumbnails[1].click();
    }
  }

  // ─── Canvas preview ──────────────────────────────────────────────────────────

  _getOrCreateCanvas() {
    if (this._canvas) {
      if (this._canvas.isConnected) return this._canvas;
      // Variant change caused Horizon to re-render slides — canvas is detached
      this._resizeObserver?.disconnect();
      this._resizeObserver = null;
      this._canvas = null;
    }

    if (!this._mediaGallery) return null;

    // Canvas goes inside slideshow-slide at index 1 so it moves with the slide during transitions
    const scroller = this._mediaGallery.querySelector('slideshow-slides');
    if (!scroller) return null;

    const targetSlide = scroller.querySelectorAll('slideshow-slide')[1];
    if (!targetSlide) return null;

    if (getComputedStyle(targetSlide).position === 'static') {
      targetSlide.style.position = 'relative';
    }

    const canvas = document.createElement('canvas');
    canvas.classList.add('personalizador-canvas');
    canvas.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 2;
    `;

    targetSlide.appendChild(canvas);
    this._canvas = canvas;

    // Sync size inline — calling _resizeCanvas() here would trigger _redrawCanvas → _getOrCreateCanvas loop
    const { width, height } = targetSlide.getBoundingClientRect();
    canvas.width = Math.round(width) || 1;
    canvas.height = Math.round(height) || 1;

    // Keep canvas size in sync on future resizes
    this._resizeObserver = new ResizeObserver(() => this._resizeCanvas());
    this._resizeObserver.observe(targetSlide);

    return canvas;
  }

  _resizeCanvas() {
    if (!this._canvas) return;
    const parent = this._canvas.parentElement;
    if (!parent) return;

    const { width, height } = parent.getBoundingClientRect();
    if (this._canvas.width !== Math.round(width) || this._canvas.height !== Math.round(height)) {
      this._canvas.width = Math.round(width);
      this._canvas.height = Math.round(height);
      this._redrawCanvas();
    }
  }

  _clearCanvas() {
    if (!this._canvas) return;
    const ctx = this._canvas.getContext('2d');
    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
  }

  _clearPreview() {
    if (this._fontAnimFrame) {
      cancelAnimationFrame(this._fontAnimFrame);
      this._fontAnimFrame = null;
    }
    this._nombreFontCurrent = null;
    this._nombreIsSmall = null;

    this._clearCanvas();
    // Remove canvas from DOM when Ninguno
    if (this._canvas && this._mode === 'ninguno') {
      this._canvas.remove();
      this._canvas = null;
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }
    // Also clear mobile preview
    this._clearMobilePreview();
  }

  _updatePreview(nombre, dorsal) {
    this._previewNombre = nombre;
    this._previewDorsal = dorsal;
    this._switchGalleryToPreviewImage();
    this._redrawCanvas();
    this._updateMobilePreview(nombre, dorsal);
  }

  _computeNombreFontSize(cw, nombre) {
    if (!nombre) return null;
    const cfg = this._config;
    const isLargeFont = nombre.length < (cfg.salto_tamano || 8);
    if (cfg.nombre_tamano != null) {
      const largePx = Math.round(cw * cfg.nombre_tamano / 100);
      // Small size = same ratio as default (0.075 / 0.11 ≈ 0.68)
      const smallPx = Math.round(cw * 0.075) - 2;
      return isLargeFont ? largePx : smallPx;
    }
    return (isLargeFont ? Math.round(cw * 0.11) : Math.round(cw * 0.075)) - 2;
  }

  _redrawCanvas() {
    const canvas = this._getOrCreateCanvas();
    if (!canvas) return;

    const nombre = this._previewNombre || '';
    const dorsal = this._previewDorsal || '';
    if (!nombre && !dorsal) {
      this._clearCanvas();
      return;
    }

    const targetFontSize = this._computeNombreFontSize(canvas.width, nombre);

    if (nombre) {
      const cfg = this._config;
      const isSmall = nombre.length >= (cfg.salto_tamano || 8);

      if (this._nombreFontCurrent === null) {
        // First draw — set directly, no animation
        this._nombreFontCurrent = targetFontSize;
        this._nombreIsSmall = isSmall;
      } else if (this._nombreIsSmall !== isSmall) {
        // Threshold crossed — animate
        this._nombreIsSmall = isSmall;
        this._animateNombreFontTo(targetFontSize, canvas);
      } else if (!this._fontAnimFrame) {
        // Same category, no animation running — snap to handle canvas resize
        this._nombreFontCurrent = targetFontSize;
      }
    } else {
      this._nombreFontCurrent = null;
      this._nombreIsSmall = null;
    }

    this._drawCanvasContent(canvas, this._nombreFontCurrent ?? targetFontSize);
  }

  _animateNombreFontTo(target, canvas) {
    if (this._fontAnimFrame) cancelAnimationFrame(this._fontAnimFrame);
    const start = this._nombreFontCurrent;
    const t0 = performance.now();
    const dur = 250;

    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const ease = p < 0.5 ? 2 * p * p : -1 + (4 - 2 * p) * p;
      this._nombreFontCurrent = start + (target - start) * ease;

      const c = this._canvas;
      if (c?.isConnected) this._drawCanvasContent(c, this._nombreFontCurrent);

      if (p < 1) {
        this._fontAnimFrame = requestAnimationFrame(tick);
      } else {
        this._fontAnimFrame = null;
        this._nombreFontCurrent = target;
      }
    };

    this._fontAnimFrame = requestAnimationFrame(tick);
  }

  _drawCanvasContent(canvas, nombreFontSize) {
    const nombre = this._previewNombre || '';
    const dorsal = this._previewDorsal || '';
    const cfg = this._config;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const cw = canvas.width;
    const ch = canvas.height;
    const color = cfg.color_tipo || '#ffffff';

    // ── Draw nombre ───────────────────────────────────────────────────────────
    if (nombre && nombreFontSize) {
      const fontNombre = cfg.tipografia_nombre || 'sans-serif';
      const fontSize = Math.round(nombreFontSize);
      ctx.font = `${fontSize}px "${fontNombre}", sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const nx = (cfg.nombre_pos_h != null ? cfg.nombre_pos_h : 50) * (cw / 100);
      const ny = (cfg.nombre_pos_v != null ? cfg.nombre_pos_v : 28) * (ch / 100);

      if (cfg.curva) {
        const curveRadius = Math.round(cw * 0.28);
        this._drawCurvedText(ctx, nombre.toUpperCase(), nx, ny + curveRadius, curveRadius, fontSize);
      } else {
        ctx.fillText(nombre.toUpperCase(), nx, ny);
      }
    }

    // ── Draw dorsal ───────────────────────────────────────────────────────────
    if (dorsal !== '' && dorsal !== undefined) {
      const fontDorsal = cfg.tipografia_dorsal || 'sans-serif';
      const dorsalFontSize = cfg.dorsal_tamano != null
        ? Math.round(cw * cfg.dorsal_tamano / 100)
        : Math.round(cw * 0.28) + 10;
      ctx.font = `${dorsalFontSize}px "${fontDorsal}", sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const dx = (cfg.dorsal_pos_h != null ? cfg.dorsal_pos_h : 50) * (cw / 100);
      const dy = (cfg.dorsal_pos_v != null ? cfg.dorsal_pos_v : 42) * (ch / 100);

      ctx.fillText(String(dorsal), dx, dy);
    }
  }

  _drawCurvedText(ctx, text, cx, cy, radius, fontSize) {
    const chars = text.split('');
    const widths = chars.map(c => ctx.measureText(c).width);
    const spacing = fontSize * 0.08;
    const totalArc = widths.reduce((s, w) => s + w + spacing, -spacing);
    const totalAngle = totalArc / radius;

    let angle = -Math.PI / 2 - totalAngle / 2;

    ctx.save();
    for (let i = 0; i < chars.length; i++) {
      const charAngle = angle + (widths[i] / 2) / radius;
      const x = cx + radius * Math.cos(charAngle);
      const y = cy + radius * Math.sin(charAngle);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(charAngle + Math.PI / 2);
      ctx.fillText(chars[i], 0, 0);
      ctx.restore();

      angle += (widths[i] + spacing) / radius;
    }
    ctx.restore();
  }

  // ─── Mobile preview ──────────────────────────────────────────────────────────

  _getMobilePreviewContainer() {
    return this.querySelector('[data-mobile-preview]');
  }

  _updateMobilePreview(nombre, dorsal) {
    const container = this._getMobilePreviewContainer();
    if (!container) return;

    container.hidden = false;

    let canvas = container.querySelector('canvas');
    if (!canvas) {
      const img = container.querySelector('img');
      if (!img) return;

      canvas = document.createElement('canvas');
      canvas.style.cssText = 'position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none;';
      container.style.position = 'relative';
      container.appendChild(canvas);

      const syncSize = () => {
        const { width, height } = img.getBoundingClientRect();
        if (width && height) {
          canvas.width = Math.round(width);
          canvas.height = Math.round(height);
        }
      };
      img.addEventListener('load', syncSize);
      syncSize();
    }

    // Sync size before drawing
    const img = container.querySelector('img');
    if (img) {
      const { width, height } = img.getBoundingClientRect();
      if (width && height) {
        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
      }
    }

    this._previewNombre = nombre;
    this._previewDorsal = dorsal;
    const nombreFontSize = this._computeNombreFontSize(canvas.width, nombre);
    this._drawCanvasContent(canvas, nombreFontSize);
  }

  _clearMobilePreview() {
    const container = this._getMobilePreviewContainer();
    if (!container) return;
    const canvas = container.querySelector('canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    container.hidden = true;
  }

  // ─── Cart intercept ──────────────────────────────────────────────────────────

  _patchProductFormSubmit() {
    const pfComp = this._productForm?.closest('product-form-component');

    if (pfComp && typeof pfComp.handleSubmit === 'function') {
      // Horizon delegates on:submit via document capture → calls instance.handleSubmit.
      // Monkey-patch so our code runs before Horizon's native cart add.
      const original = pfComp.handleSubmit.bind(pfComp);
      this._pfComp = pfComp;
      pfComp.handleSubmit = (event) => {
        if (this._mode === 'ninguno') return original(event);
        event.preventDefault();
        this._runCartAdd();
      };
    } else if (this._productForm) {
      // Fallback for non-Horizon themes
      this._productForm.addEventListener('submit', (e) => {
        if (this._mode === 'ninguno') return;
        e.preventDefault();
        this._runCartAdd();
      }, { capture: true });
    }
  }

  _runCartAdd() {
    const error = this._validate();
    if (error) { this._showError(error); return; }
    this._hideError();
    this._setLoading(true);
    const baseVariantId = this._productForm.querySelector('[name="id"]').value;
    const quantity = parseInt(this._productForm.querySelector('[name="quantity"]')?.value || '1', 10);
    if (this._mode === 'jugador') {
      this._addJugadorToCart(baseVariantId, quantity);
    } else {
      this._addPersonalizadoToCart(baseVariantId, quantity);
    }
  }

  _validate() {
    const cfg = this._config;

    if (this._mode === 'jugador') {
      if (!this._jugadorSeleccionado) {
        return 'Selecciona un jugador para continuar.';
      }
    }

    if (this._mode === 'personalizado') {
      const tipo = this._tipoManual;
      const nombre = this.querySelector('[data-nombre-input]')?.value.trim() || '';
      const dorsal = this.querySelector('[data-dorsal-input]')?.value.trim() || '';

      if (tipo !== 'dorsal' && !nombre) {
        return 'Introduce un nombre para continuar.';
      }
      if (tipo !== 'nombre' && !dorsal) {
        return 'Introduce un dorsal para continuar.';
      }
      if (nombre && nombre.length > (cfg.max_chars || 12)) {
        return `El nombre no puede superar ${cfg.max_chars || 12} caracteres.`;
      }
      const dorsalNum = parseInt(dorsal, 10);
      if (tipo !== 'nombre' && (isNaN(dorsalNum) || dorsalNum < 0 || dorsalNum > 99)) {
        return 'El dorsal debe ser un número entre 0 y 99.';
      }
    }

    return null;
  }

  _getBaseProductLabel() {
    const cfg = this._config;
    const title = cfg.product_title || '';
    const pressedBtn = document.querySelector('[data-option-value][aria-pressed="true"]');
    const variantLabel = pressedBtn?.textContent?.trim() || '';
    return variantLabel ? `${title} - ${variantLabel}` : title;
  }

  _getSelectedTalla() {
    // Button/swatch variant style
    const checkedInputs = [...document.querySelectorAll('variant-picker fieldset input:checked')];
    if (checkedInputs.length) {
      return checkedInputs
        .map(input => input.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent?.trim())
        .filter(Boolean)
        .join(' / ');
    }
    // Dropdown variant style
    const selects = [...document.querySelectorAll('variant-picker select')];
    if (selects.length) {
      return selects
        .map(s => s.options[s.selectedIndex]?.text?.trim())
        .filter(Boolean)
        .join(' / ');
    }
    return '';
  }

  async _addJugadorToCart(baseVariantId, quantity) {
    const cfg = this._config;
    const jugador = this._jugadorSeleccionado;

    const baseId = parseInt(baseVariantId, 10);
    const items = [
      {
        id: cfg.prod_jugador.variant_id,
        quantity,
        parent_id: baseId,
        properties: {
          'Nombre': jugador.nombre,
          'Dorsal': String(jugador.dorsal),
          'Producto': this._getBaseProductLabel(),
          'Talla': this._getSelectedTalla(),
        },
      },
      {
        id: baseId,
        quantity,
      },
    ];

    await this._postToCart(items);
  }

  async _addPersonalizadoToCart(baseVariantId, quantity) {
    const cfg = this._config;
    const tipo = this._tipoManual;
    const nombre = this.querySelector('[data-nombre-input]')?.value.trim() || '';
    const dorsal = this.querySelector('[data-dorsal-input]')?.value.trim() || '';

    const variant = cfg.prod_manual.variants.find(v => v.tipo === tipo) || cfg.prod_manual.variants[0];
    const baseId = parseInt(baseVariantId, 10);

    const properties = { 'Producto': this._getBaseProductLabel(), 'Talla': this._getSelectedTalla() };
    if (tipo !== 'dorsal') properties['Nombre'] = nombre;
    if (tipo !== 'nombre') properties['Dorsal'] = dorsal;

    const items = [
      {
        id: variant.id,
        quantity,
        parent_id: baseId,
        properties,
      },
      {
        id: baseId,
        quantity,
      },
    ];

    await this._postToCart(items);
  }

  async _postToCart(items) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('[personalizador] cart/add.js error', res.status, text.slice(0, 200));
        let msg = 'Error al añadir al carrito.';
        try { msg = JSON.parse(text).description || JSON.parse(text).message || msg; } catch (_) {}
        throw new Error(msg);
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: 'personalizador',
            data: { source: 'personalizador' },
          },
        })
      );
    } catch (err) {
      this._showError(err.message || 'Error al añadir al carrito.');
    } finally {
      this._setLoading(false);
    }
  }

  // ─── UI feedback ─────────────────────────────────────────────────────────────

  _showError(msg) {
    const el = this.querySelector('[data-personalizador-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  _hideError() {
    const el = this.querySelector('[data-personalizador-error]');
    if (el) el.hidden = true;
  }

  _setLoading(loading) {
    const btn = this._productForm?.querySelector('[name="add"]');
    if (btn) btn.disabled = loading;
  }
}

customElements.define('personalizador-block', PersonalizadorBlock);
